<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import QuizEngine from "~/features/computer/components/QuizEngine.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { loopsExercises } from "~/features/computer/data/exercises/loops.exercises";
import { ch2Questions } from "~/features/python/ch2/exercise/data/ch2.questions";
import { pythonInteractiveCards } from "~/features/python/data/chapter2-interactive";

const relatedCards = pythonInteractiveCards.filter((item) => item.to !== "/11th/computer/python/loops/");
const loopProgramQuestions = ch2Questions.filter(
  (question) => question.topic === "loops" || question.concepts.includes("loops"),
);

usePageSeo({
  title: "Chapter 2 Python Loops | Ustaad",
  description: "Chapter 2 Python loops with for loop and while loop MCQs, short notes and board-style practice questions for class 11 students.",
  keywords: "chapter 2 python loops, python loops class 11, for loop while loop 11th class computer",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Ch 2 Python", item: "/11th/computer/python/" },
    { name: "Loops", item: "/11th/computer/python/loops/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "Chapter 2 Python Loops",
    items: [
      { name: "Chapter 2 Python", path: "/11th/computer/python/" },
      { name: "Chapter 2 Python MCQs", path: "/11th/computer/python/mcqs/" },
      { name: "Chapter 2 Python Short Questions", path: "/11th/computer/python/short-questions/" },
      { name: "Chapter 2 Python Long Questions", path: "/11th/computer/python/long-questions/" },
      { name: "Chapter 2 Python Programs", path: "/11th/computer/python/programs/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="rounded-3xl bg-slate-950 p-6 text-white">
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-sky-300">Python Chapter 2</p>
        <h1 class="mt-2 text-2xl font-bold tracking-tight">Chapter 2 Python Loops</h1>
        <p class="mt-3 text-sm leading-7 text-slate-200">
          For loop aur while loop ko exam point of view se samjho. Yahan MCQs, quick answers aur board-style loop questions ek jagah milte hain.
        </p>
      </section>

      <section class="grid gap-6 lg:grid-cols-2">
        <article class="rounded-3xl border border-slate-200 bg-white p-5">
          <h2 class="text-lg font-semibold text-slate-900">Loop shortcut</h2>
          <div class="mt-4 space-y-3 text-sm leading-7 text-slate-700">
            <p>- For loop tab use hota hai jab count ya items clear hon</p>
            <p>- While loop tab useful hota hai jab condition-based repetition chahiye ho</p>
            <p>- Odd/even, range aur repeated output loop-based repeat questions hote hain</p>
          </div>
        </article>

        <article class="rounded-3xl border border-amber-200 bg-amber-50 p-5">
          <h2 class="text-lg font-semibold text-slate-900">Exam focus</h2>
          <div class="mt-4 space-y-3 text-sm leading-7 text-slate-700">
            <p>- For loop aur while loop ka farq</p>
            <p>- range() ka role</p>
            <p>- Odd numbers / repeated output programs</p>
          </div>
        </article>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5">
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-slate-900">Loops MCQs</h2>
          <p class="text-sm text-slate-600">Pehle objective concepts clear karein, phir program questions attempt karein.</p>
        </div>
        <div class="mt-4">
          <QuizEngine :questions="loopsExercises.mcqs" />
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5">
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-slate-900">Important Short Answers</h2>
          <p class="text-sm text-slate-600">Yeh short questions exam me direct kaam aate hain.</p>
        </div>
        <div class="mt-4 grid gap-3">
          <article
            v-for="item in loopsExercises.short"
            :key="item.id"
            class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200"
          >
            <h3 class="text-sm font-semibold text-slate-900">{{ item.question }}</h3>
            <p class="mt-2 text-sm leading-6 text-slate-600">{{ item.answer }}</p>
          </article>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5">
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-slate-900">Board-style Loop Questions</h2>
          <p class="text-sm text-slate-600">Loop-based practical aur long-question style prompts yahan se kholo.</p>
        </div>

        <div class="mt-4 grid gap-3">
          <NuxtLink
            v-for="question in loopProgramQuestions"
            :key="question.id"
            :to="`/11th/computer/python/programs/#q-${question.id}`"
            class="rounded-2xl border border-slate-200 bg-slate-50 p-4 transition hover:border-sky-300 hover:bg-sky-50"
          >
            <div class="flex flex-wrap items-center justify-between gap-3">
              <h3 class="text-sm font-semibold text-slate-900">Q{{ question.number }}. {{ question.question }}</h3>
              <span class="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
                {{ question.type }}
              </span>
            </div>
          </NuxtLink>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-slate-50 p-5">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">More Topics</p>
          <h2 class="mt-2 text-lg font-semibold text-slate-900">More Python Topics</h2>
        </div>
        <div class="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <NuxtLink
            v-for="item in relatedCards"
            :key="item.to"
            :to="item.to"
            class="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-sky-300 hover:bg-sky-50"
          >
            <h3 class="text-sm font-semibold text-slate-900">{{ item.title }}</h3>
            <p class="mt-2 text-sm leading-6 text-slate-600">{{ item.description }}</p>
          </NuxtLink>
        </div>
      </section>
    </div>
  </PageContainer>
</template>
